/**
 * `<tag>…</tag>` 마크업 문자열을 구간 리스트로 분해하는 순수 파서.
 *
 * `Text`의 `labelStyle`가 구간별로 다른 variant를 입히기 위해 사용한다. 파싱
 * 결과는 React 텍스트 노드와 styled span으로만 렌더되므로(HTML 주입 없음) XSS
 * 위험이 없다. 중첩 태그는 지원하지 않는다.
 */

/** 마크업 문자열을 분해한 한 구간. */
export type RichLabelSegment = {
  /** 렌더할 텍스트. */
  text: string;
  /** 이 구간을 감싼 태그 이름. 평문 구간이면 null. */
  tag: string | null;
};

// 짝이 맞는 `<name>…</name>`만 인식한다. 역참조(\1)로 같은 이름의 닫는 태그를
// 강제하고, 내부는 비탐욕(*?)이라 중첩 없이 가장 안쪽 한 겹만 잡는다.
const PAIRED_TAG_PATTERN = /<([a-zA-Z][\w-]*)>([\s\S]*?)<\/\1>/g;

/**
 * 마크업 문자열을 평문/태그 구간으로 분해한다.
 *
 * 짝이 맞는 `<name>…</name>`만 태그 구간으로 보고, 그 밖의 `<`·`>`는 평문으로
 * 남긴다. 매칭되는 태그가 없으면 원본 전체가 하나의 평문 구간이 된다.
 *
 * @param source 분해할 원본 문자열.
 * @returns 등장 순서대로의 구간 리스트. 빈 문자열이면 빈 배열.
 */
export function parseRichLabel(source: string): RichLabelSegment[] {
  const segments: RichLabelSegment[] = [];
  let lastIndex = 0;

  for (const match of source.matchAll(PAIRED_TAG_PATTERN)) {
    const matchStart = match.index ?? lastIndex;
    // 태그 앞의 평문 구간 보존
    if (matchStart > lastIndex) {
      segments.push({ text: source.slice(lastIndex, matchStart), tag: null });
    }
    segments.push({ text: match[2], tag: match[1] });
    lastIndex = matchStart + match[0].length;
  }

  // 마지막 태그 뒤의 평문(또는 태그가 전혀 없을 때의 원본 전체)
  if (lastIndex < source.length) {
    segments.push({ text: source.slice(lastIndex), tag: null });
  }

  return segments;
}
